import React, { useId } from "react";
import { Image, Platform, Pressable, StyleSheet, View, useWindowDimensions, type PressableProps, type StyleProp, type ViewStyle } from "react-native";
import Svg, { Circle, Defs, Ellipse, G, LinearGradient, RadialGradient, Stop } from "react-native-svg";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { floorPalette, SANS_SEMI } from "@/features/floor/palette";
import { BorderRadius } from "@/constants/theme";

const web = (style: Record<string, string>): ViewStyle => Platform.OS === "web" ? style as ViewStyle : {};
export function brandCanvas(dark: boolean): ViewStyle {
  return web({ backgroundImage: dark
    ? "radial-gradient(ellipse at 95% 0%, #263136 0%, transparent 48%)"
    : "radial-gradient(ellipse at 100% 0%, #e2e9e9 0%, transparent 45%), radial-gradient(ellipse at 0% 40%, #eee3ce 0%, transparent 50%)" });
}
// No backdrop blur on scrolling lists: translucency and edge lighting carry
// depth without putting a GPU blur behind every row.
export function brandSurface(dark: boolean, raised = false): ViewStyle {
  return {
    backgroundColor: dark ? "#242a2c" : "#fffdf8",
    borderWidth: 1,
    borderColor: dark ? "#465052" : "#ddd8cc",
    borderTopColor: dark ? "#657072" : "#ffffff",
    borderRadius: BorderRadius.xl,
    ...web({
      backgroundImage: dark ? "linear-gradient(145deg, rgba(65,77,79,.5), rgba(26,31,33,.85))" : "linear-gradient(145deg, rgba(255,255,255,.96), rgba(250,246,236,.82))",
      boxShadow: dark ? "0 8px 22px -16px #000, inset 0 1px 0 #ffffff12" : `0 ${raised ? "14px 30px -18px" : "6px 16px -13px"} #4d483f55, inset 0 1px 0 #fff`,
    }),
  };
}

export function DepthPressable({ style, onPressIn, onPressOut, ...props }: Omit<PressableProps, "style"> & { style?: StyleProp<ViewStyle> }) {
  // Today is the first paint on the web. CSS owns the hover/press affordance
  // here so a reader does not have to parse the Reanimated runtime before the
  // first market verdict can render. Native keeps the physical version in the
  // platform-neutral source file.
  return <Pressable {...props} style={style}
    onPressIn={onPressIn}
    onPressOut={onPressOut} />;
}

/** A decorative metal-and-glass brand sculpture. It encodes no market data. */
export function BrandOrbit({ size = 150, tone = "gold" }: { size?: number; tone?: "gold" | "blue" | "green" }) {
  const id = useId().replace(/:/g, "");
  const metal = tone === "blue" ? "#9fc6dc" : tone === "green" ? "#a5cbb9" : "#dfc28e";
  return <View pointerEvents="none" accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ width: size, height: size }}>
    <Svg width={size} height={size} viewBox="0 0 200 200" aria-hidden>
      <Defs>
        <LinearGradient id={`${id}metal`} x1="0" y1="0" x2="1" y2="1"><Stop offset="0" stopColor="#fff4d9" /><Stop offset=".22" stopColor={metal} /><Stop offset=".48" stopColor="#506467" /><Stop offset=".7" stopColor={metal} /><Stop offset="1" stopColor="#29363c" /></LinearGradient>
        <RadialGradient id={`${id}core`} cx=".32" cy=".25" r=".8"><Stop offset="0" stopColor="#829a9e" /><Stop offset=".32" stopColor="#334a54" /><Stop offset="1" stopColor="#0c171f" /></RadialGradient>
        <RadialGradient id={`${id}halo`}><Stop offset="0" stopColor={metal} stopOpacity=".25" /><Stop offset="1" stopColor={metal} stopOpacity="0" /></RadialGradient>
      </Defs>
      <Circle cx="100" cy="100" r="98" fill={`url(#${id}halo)`} />
      <Ellipse cx="100" cy="164" rx="55" ry="9" fill="#000" opacity=".2" />
      <G rotation="-32" origin="100,100">
        <Ellipse cx="100" cy="100" rx="88" ry="43" fill="none" stroke="#07141b" strokeWidth="12" />
        <Ellipse cx="100" cy="97" rx="88" ry="43" fill="none" stroke={`url(#${id}metal)`} strokeWidth="8" />
        <Ellipse cx="100" cy="95" rx="88" ry="43" fill="none" stroke="#fff4d9" strokeOpacity=".5" strokeWidth="1" />
      </G>
      <Circle cx="100" cy="98" r="48" fill={`url(#${id}core)`} stroke={`url(#${id}metal)`} strokeWidth="3" />
      <Ellipse cx="100" cy="97" rx="26" ry="68" rotation="28" origin="100,100" fill="none" stroke={`url(#${id}metal)`} strokeWidth="3" opacity=".8" />
      <Circle cx="159" cy="59" r="6" fill={metal} stroke="#fff2cc" strokeWidth="1" />
    </Svg>
    <Image source={require("../../assets/images/brand-mark-128.webp")} style={{ position: "absolute", left: size * .405, top: size * .39, width: size * .19, height: size * .19, borderRadius: size * .04 }} />
  </View>;
}

export function BrandIntro({ eyebrow, title, description, tone = "gold", children }: { eyebrow: string; title: string; description: string; tone?: "gold" | "blue" | "green"; children?: React.ReactNode }) {
  const { width } = useWindowDimensions();
  const wide = width >= 900;
  const light = tone === "blue" ? "#b8d8e8" : tone === "green" ? "#b7d9c8" : "#e2c891";
  return <View style={[styles.intro, web({ backgroundImage: `radial-gradient(ellipse at 100% 0%, ${tone === "blue" ? "#3d5e73" : tone === "green" ? "#355a50" : "#655a40"} 0%, transparent 63%), linear-gradient(130deg,#17252e,#142027 65%,#23343b)`, boxShadow: "0 20px 38px -22px #12232e88, inset 0 1px 0 #ffffff40" })]}>
    <View style={[styles.sculpture, { right: wide ? 18 : -4, top: wide ? 8 : -16 }]}><BrandOrbit size={wide ? 210 : 90} tone={tone} /></View>
    <View style={{ gap: 12, paddingRight: wide ? 215 : 0 }}>
      <ThemedText style={{ color: light, fontSize: 12, letterSpacing: 1.8, fontWeight: "700", paddingRight: wide ? 0 : 80, minHeight: wide ? 28 : 40 }}>{eyebrow}</ThemedText>
      <ThemedText accessibilityRole="header" style={{ color: "#fff9ed", fontFamily: SANS_SEMI, fontWeight: "600", fontSize: wide ? 34 : 26, lineHeight: wide ? 42 : 34, paddingRight: wide ? 0 : 36 }}>{title}</ThemedText>
      <ThemedText style={{ color: "#e1e6e5", fontSize: 15, lineHeight: 23 }}>{description}</ThemedText>
      {children}
    </View>
  </View>;
}

export function ChapterLabel({ number, title }: { number: string; title: string }) {
  const p = floorPalette(useTheme().isDark);
  return <View style={styles.chapter}><View style={[styles.number, { backgroundColor: p.accentWash, borderColor: p.ln }]}><ThemedText style={{ color: p.goldT, fontSize: 12, fontWeight: "700" }}>{number}</ThemedText></View><ThemedText style={{ color: p.mut, fontSize: 12, fontWeight: "600", letterSpacing: 1.3 }}>{title}</ThemedText></View>;
}
const styles = StyleSheet.create({
  intro: { backgroundColor: "#17252e", borderRadius: BorderRadius.outer, borderWidth: 1, borderColor: "#68716c", padding: 24, overflow: "hidden", minWidth: 0 },
  sculpture: { position: "absolute", opacity: .9 },
  chapter: { flexDirection: "row", alignItems: "center", gap: 9, marginBottom: 8 },
  number: { width: 29, height: 29, borderRadius: 10, borderWidth: 1, alignItems: "center", justifyContent: "center" },
});
